import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Chip, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, TextField, Stack, Autocomplete, } from '@mui/material';
import { RestartAlt as RestartAltIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_URL, ACCENT } from './shared';
import { TH } from './attendanceCommon';
// HR-only admin view: links each active employee's Onboarding record to
// their attendance-machine employee code (Onboarding.empId is a different
// numbering system, never the machine code — see routes/attendance.js).
// Deliberately lives only here, not on the onboarding form, since it's an
// attendance-system concern HR manages separately and as needed, not a
// one-time onboarding step.
export function AttendanceCodeMapping() {
    const [roster, setRoster] = useState([]);
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState('');
    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [rosterRes, mappingRes] = await Promise.all([
                axios.get(`${API_URL}/attendance/roster`),
                axios.get(`${API_URL}/attendance/code-mapping`),
            ]);
            setRoster(rosterRes.data.data || []);
            setRows(mappingRes.data.data || []);
        }
        catch (e) {
            setError(e.response?.data?.message || 'Failed to load employee codes');
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);
    const save = async (row, code) => {
        setSavingId(row.onboardingId);
        setError('');
        try {
            const res = await axios.patch(`${API_URL}/attendance/code-mapping/${row.onboardingId}`, { attendanceCode: code });
            setRows(prev => prev.map(r => r.onboardingId === row.onboardingId
                ? { ...r, attendanceCode: res.data.data.attendanceCode, suggestedCode: res.data.data.attendanceCode ? null : r.suggestedCode }
                : r));
        }
        catch (e) {
            setError(e.response?.data?.message || 'Could not save that code');
        }
        finally {
            setSavingId(null);
        }
    };
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q)
            return rows;
        return rows.filter(r => r.name.toLowerCase().includes(q) || r.dept.toLowerCase().includes(q) || r.empId.includes(q));
    }, [rows, search]);
    const unmatchedCount = rows.filter(r => !r.attendanceCode && !r.suggestedCode).length;
    return (_jsxs(Box, { children: [_jsxs(Stack, { direction: "row", spacing: 1.5, sx: { mb: 2 }, alignItems: "center", flexWrap: "wrap", useFlexGap: true, children: [_jsx(TextField, { size: "small", label: "Search name / department", value: search, onChange: e => setSearch(e.target.value), sx: { minWidth: 240 } }), _jsx(Button, { startIcon: _jsx(RestartAltIcon, {}), onClick: load, sx: { textTransform: 'none', color: ACCENT }, children: "Refresh" }), unmatchedCount > 0 && (_jsx(Chip, { size: "small", color: "warning", label: `${unmatchedCount} with no code and no name match` }))] }), error && _jsx(Alert, { severity: "error", sx: { mb: 2 }, children: error }), loading && _jsx(Box, { sx: { py: 4, textAlign: 'center' }, children: _jsx(CircularProgress, { size: 22 }) }), !loading && (_jsx(TableContainer, { sx: { bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 1.5 }, children: _jsxs(Table, { size: "small", children: [_jsx(TableHead, { children: _jsx(TableRow, { children: ['Employee', 'Department', 'Onboarding ID', 'Attendance Code'].map(h => _jsx(TableCell, { sx: TH, children: h }, h)) }) }), _jsx(TableBody, { children: filtered.map(row => {
                                const current = roster.find(o => o.code === row.attendanceCode) || null;
                                const busy = savingId === row.onboardingId;
                                return (_jsxs(TableRow, { hover: true, children: [_jsx(TableCell, { sx: { fontSize: 13, fontWeight: 600 }, children: row.name || '—' }), _jsx(TableCell, { sx: { fontSize: 13 }, children: row.dept || '—' }), _jsx(TableCell, { sx: { fontSize: 13 }, children: row.empId || '—' }), _jsx(TableCell, { sx: { minWidth: 260 }, children: _jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [_jsx(Autocomplete, { size: "small", sx: { width: 220 }, options: roster, value: current, loading: busy, disabled: busy, getOptionLabel: o => `${o.code} — ${o.name}`, isOptionEqualToValue: (a, b) => a.code === b.code, onChange: (_, v) => save(row, v ? v.code : ''), renderInput: params => _jsx(TextField, { ...params, placeholder: "Assign a code\u2026" }) }), !row.attendanceCode && row.suggestedCode && (_jsxs(Button, { size: "small", disabled: busy, onClick: () => save(row, row.suggestedCode), sx: { textTransform: 'none', fontSize: '0.7rem', color: ACCENT }, children: ["Use suggested (", row.suggestedCode, ")"] })), !row.attendanceCode && !row.suggestedCode && (_jsx(Typography, { fontSize: 12, color: "text.secondary", children: "No name match" }))] }) })] }, row.onboardingId));
                            }) })] }) }))] }));
}
